import type { Snapshot, SnapshotDelta } from '@jalsa/shared';

import type { CheckMode, CheckResponse, Station } from './types';

/**
 * Ergebnis eines API-Aufrufs.
 *
 * `network` wird bewusst von `error` und `unauthorized` getrennt: eine
 * abgebrochene Verbindung ist eine Aussage über die Leitung, kein Urteil
 * über die Person. Die Oberfläche darf beides nie gleich darstellen.
 * sonst weist die Duty-Kraft berechtigte Personen ab, weil das WLAN hakt.
 */
export type ApiOutcome<T> =
  | { ok: true; data: T }
  | { ok: false; kind: 'network' }
  | { ok: false; kind: 'unauthorized' }
  | { ok: false; kind: 'error'; message: string };

export interface CheckRequest {
  mode: CheckMode;
  query?: string;
  personId?: string;
  clientEventId: string;
  clientAt: string;
}

export interface Api {
  /** Der Gerätename kommt vom Platz im Office, nicht mehr vom Gerät. */
  join(joinCode: string): Promise<ApiOutcome<{ station: Station; slot: { id: string; name: string } }>>;
  me(): Promise<ApiOutcome<{ stationId: string; stationName: string; area: string }>>;
  check(request: CheckRequest): Promise<ApiOutcome<CheckResponse>>;
  snapshot(): Promise<ApiOutcome<Snapshot>>;
  snapshotDelta(since: string): Promise<ApiOutcome<SnapshotDelta>>;
  syncChecks(events: unknown[]): Promise<ApiOutcome<SyncResponse>>;
}

export interface SyncResponse {
  accepted: number;
  duplicates: number;
  results: { clientEventId: string; status: string }[];
}

export function createApi(baseUrl = ''): Api {
  async function call<T>(path: string, init?: RequestInit): Promise<ApiOutcome<T>> {
    let response: Response;
    try {
      response = await fetch(`${baseUrl}${path}`, {
        // Das Gerätetoken liegt als httpOnly-Cookie. JavaScript kommt nicht
        // heran und muss es auch nicht.
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        ...init,
      });
    } catch {
      return { ok: false, kind: 'network' };
    }

    if (response.status === 401) return { ok: false, kind: 'unauthorized' };

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }

    if (!response.ok) {
      const message =
        (body as { message?: string | string[] } | undefined)?.message ??
        `Fehler ${response.status}`;
      return { ok: false, kind: 'error', message: Array.isArray(message) ? message[0] : message };
    }

    return { ok: true, data: body as T };
  }

  return {
    join: (joinCode) =>
      call('/v1/stations/join', { method: 'POST', body: JSON.stringify({ joinCode }) }),
    me: () => call('/v1/stations/me'),
    check: (request) => call('/v1/check', { method: 'POST', body: JSON.stringify(request) }),
    snapshot: () => call('/v1/snapshot'),
    snapshotDelta: (since) => call(`/v1/snapshot/delta?since=${encodeURIComponent(since)}`),
    syncChecks: (events) =>
      call('/v1/checks/sync', { method: 'POST', body: JSON.stringify({ events }) }),
  };
}
